import { useEffect, useState } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

/**
 * Renders PayPal Smart Buttons for a DealHub payment kind.
 * Props:
 *   payload: { lookup_key?, tip_amount_cents?, tip_contributor_id?, boost_deal_id? }
 *   onPaid: (record) => void
 *   testIdPrefix: string
 *   disabled: bool
 */
export default function PayPalCheckoutButton({ payload, onPaid, testIdPrefix = "paypal", disabled = false }) {
  const [cfg, setCfg] = useState({ configured: false, client_id: "", mode: "sandbox", loading: true });

  useEffect(() => {
    let alive = true;
    api.get("/paypal/config")
      .then(({ data }) => { if (alive) setCfg({ ...data, loading: false }); })
      .catch(() => { if (alive) setCfg({ configured: false, client_id: "", mode: "sandbox", loading: false }); });
    return () => { alive = false; };
  }, []);

  if (cfg.loading) {
    return <div className="mt-3 text-xs font-mono uppercase tracking-widest text-black/40" data-testid={`${testIdPrefix}-loading`}>Loading PayPal…</div>;
  }
  if (!cfg.configured) {
    return (
      <div className="mt-3 border border-dashed border-black/25 px-3 py-2 text-[11px] font-mono uppercase tracking-widest text-black/40 text-center" data-testid={`${testIdPrefix}-not-configured`}>
        PayPal coming soon
      </div>
    );
  }

  return (
    <div className="mt-3" data-testid={`${testIdPrefix}-wrapper`}>
      <PayPalScriptProvider options={{ clientId: cfg.client_id, currency: "USD", intent: "capture" }}>
        <PayPalButtons
          disabled={disabled}
          style={{ layout: "horizontal", color: "gold", shape: "rect", label: "paypal", tagline: false, height: 40 }}
          createOrder={async () => {
            try {
              const { data } = await api.post("/payments/paypal/create-order", payload);
              return data.order_id;
            } catch (err) {
              toast.error(formatApiError(err));
              throw err;
            }
          }}
          onApprove={async (data) => {
            try {
              const { data: result } = await api.post(`/payments/paypal/capture-order/${data.orderID}`);
              if (result.payment_status === "paid") {
                toast.success("Payment confirmed ✓");
                if (typeof onPaid === "function") onPaid(result);
              } else {
                toast.error("PayPal capture pending — please refresh in a moment.");
              }
            } catch (err) {
              toast.error(formatApiError(err));
            }
          }}
          onError={(err) => {
            toast.error("PayPal error. Please try again.");
            console.error(err);
          }}
          onCancel={() => {
            toast.message("PayPal payment cancelled.");
          }}
        />
      </PayPalScriptProvider>
    </div>
  );
}
